import {
  breakpointSizes,
  getBreakpointUp,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment } from 'react'
import SvgRejectTrash from 'src/app/ExpenseReport/SvgRejectTrash'
import {
  ExpenseReportsFiltersBody,
  ExpenseReportsListCheckboxProps,
} from 'src/components/ExpenseReportsBox/ExpenseReportsBox'
import FloatingToolbar from 'src/components/FloatingToolbar/FloatingToolbar'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SubmitExpensesDataGrid from 'src/components/SubmitExpensesDataGrid/SubmitExpensesDataGrid'
import SvgCheck from 'src/components/Svgs/SvgCheck'
import SvgClose from 'src/components/Svgs/SvgClose'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import TestId from 'src/components/TestId/TestId'
import Trns from 'src/localization/Trns'
import { ExpenseReportModel } from 'src/models/expenseReport.model'
import {
  useQueryApproverOOPEs,
  useQueryNeedsManagerApprovalOOPEs,
  useQuerySubmitterOOPEs,
} from 'src/queries/submitExpenses.queries'
import { css } from 'styled-components'

interface FullExpenseReportTableProps {
  expenseReports: ExpenseReportModel[]
  bulkSelected: boolean
  selectedExpenseReports: ExpenseReportModel[]
  handleExpenseReportsCheckboxClick?: (option: ExpenseReportModel) => void
  handleBulkExpenseReportsCheckboxClick?: () => void
  handleReportBulkAction?: () => void
  handleCloseToolbar?: () => void
  allReports: boolean
  complete: boolean
  showCheckbox: boolean
  filters: ExpenseReportsFiltersBody
  checkboxProps?: ExpenseReportsListCheckboxProps
  context: 'submitter' | 'approver' | 'manager'
}

const FullExpenseReportTable: React.FC<FullExpenseReportTableProps> = (
  props,
) => {

  // use context to determine which fetch method to use
  const useQueryOOPEs =
    props.context === 'approver'
      ? useQueryApproverOOPEs
      : props.context === 'submitter'
        ? useQuerySubmitterOOPEs
        : useQueryNeedsManagerApprovalOOPEs
  const { data, fetchNextPage, isFetching } = useQueryOOPEs({
    filters: props.filters,
    enabled: true,
  })

  const complete =
    data?.numberOfPages !== undefined &&
    data?.page !== undefined &&
    data?.numberOfPages === data?.page + 1 // +1 for 0-indexing
  const isEmpty = complete && !isFetching && data?.expenseReports?.length === 0

  let emptyStateDesign
  switch (props.context) {
    case 'approver':
      emptyStateDesign = TableEmptyStateDesigns.ApproverExpenseReports
      break
    case 'manager':
      emptyStateDesign = TableEmptyStateDesigns.ExpensesNeedsApproval
      break
    case 'submitter':
    default:
      emptyStateDesign = TableEmptyStateDesigns.SubmitterExpenseReports
      break
  }

  return (
    <div
      css={css`
        width: 100%;
        background-color: ${styleConstants.colors.gray_lightest};
      `}
    >
      {!isEmpty && (
        <Fragment>
          <SubmitExpensesDataGrid
            expenseReports={
              data?.expenseReports || ([] as ExpenseReportModel[])
            }
            infiniteScrollProps={{
              fetch: () => fetchNextPage(),
              loading: isFetching,
              complete: complete,
              page: data?.page,
            }}
            filters={props.filters}
            checkboxProps={props.checkboxProps}
            context={props.context}
          />
          {props.context !== 'manager' && (
            <FloatingToolbar
              noPadding
              noColumnGap
              visible={
                props.selectedExpenseReports !== undefined &&
                props.selectedExpenseReports.length > 0
              }
              css={css`
                display: flex;
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
                align-items: center;
                ${getBreakpointUp(breakpointSizes.MD)(css`
                  height: 60px;
                `)}
              `}
            >
              <span
                css={css`
                  padding: 20px 40px;
                `}
              >
                {props.selectedExpenseReports.length > 1 ? (
                  <Trns
                    source={'${expenseReports} expense reports selected'}
                    expressions={{
                      expenseReports:
                        props.selectedExpenseReports.length.toString(),
                    }}
                  />
                ) : (
                  <Trns
                    source={'1 expense report selected'}
                    expressions={{
                      expenses: props.selectedExpenseReports.length.toString(),
                    }}
                  />
                )}
              </span>
              <>
                {props.context === 'approver' ? (
                  <TestId
                    testId={'markAsPaidButton'}
                    css={css`
                      height: 100%;
                      align-items: center;
                      border-left: 1px solid
                        ${styleConstants.colors.gray_medium};
                      border-right: 1px solid
                        ${styleConstants.colors.gray_medium};
                    `}
                  >
                    <HoverableLink
                      css={css`
                        height: 100%;
                        padding: 8px 8px;
                      `}
                      onClick={props.handleReportBulkAction}
                      ariaLabel="Mark as Paid"
                      regular
                    >
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 4px;
                          padding: 10px;
                          border-radius: 4px;
                          ${getFontCss(
                            16,
                            themeConstants.colors.cta,
                            styleConstants.fontFamilies.regular,
                          )};
                          &:hover {
                            background-color: ${styleConstants.colors
                              .blue_opacity};
                          }
                        `}
                      >
                        <SvgCheck fill={'var(--color-cta)'} />
                        <Trns
                          css={css`
                            margin-left: 8px;
                          `}
                          source={'Mark as Paid'}
                        />
                      </div>
                    </HoverableLink>
                  </TestId>
                ) : (
                  <TestId
                    testId={'deleteButton'}
                    css={css`
                      height: 100%;
                      align-items: center;
                      border-left: 1px solid
                        ${styleConstants.colors.gray_medium};
                      border-right: 1px solid
                        ${styleConstants.colors.gray_medium};
                    `}
                  >
                    <HoverableLink
                      css={css`
                        height: 100%;
                        padding: 8px 8px;
                      `}
                      onClick={props.handleReportBulkAction}
                      ariaLabel="Delete"
                    >
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 4px;
                          padding: 10px;
                          border-radius: 4px;
                          ${getFontCss(
                            16,
                            styleConstants.colors.red,
                            styleConstants.fontFamilies.regular,
                          )};
                          &:hover {
                            background-color: ${styleConstants.colors
                              .blue_opacity};
                          }
                        `}
                      >
                        <SvgRejectTrash fill={styleConstants.colors.red} />
                        <Trns
                          css={css`
                            margin-left: 8px;
                          `}
                          source={'Delete'}
                        />
                      </div>
                    </HoverableLink>
                  </TestId>
                )}
                <HoverableLink
                  ariaLabelSource={'Close'}
                  onClick={props.handleCloseToolbar}
                >
                  <div
                    css={css`
                      display: flex;
                      position: relative;
                      width: 36px;
                      height: 36px;
                      align-items: center;
                      justify-content: center;
                      margin: 0px 8px;
                      svg {
                        transition: stroke 0.2s ease;
                      }
                      svg path {
                        stroke: ${themeConstants.colors.font_primary};
                        transition: stroke-width 0.2s ease;
                      }
                      &:hover {
                        svg path {
                          stroke: ${themeConstants.colors.cta};
                        }
                        &::before {
                          content: '';
                          position: absolute;
                          top: 0;
                          left: 0;
                          width: 100%;
                          height: 100%;
                          background-color: rgba(0, 0, 0, 0.05);
                          border-radius: 8px;
                          transition: background-color 0.2s ease;
                        }
                      }
                    `}
                  >
                    <SvgClose />
                  </div>
                </HoverableLink>
              </>
            </FloatingToolbar>
          )}
        </Fragment>
      )}
      {isEmpty && <TableEmptyState design={emptyStateDesign} wide />}
    </div>
  )
}
export default FullExpenseReportTable
